import * as ex from 'excalibur';

// Matches the standard JSON Hash format from free-tex-packer
export interface TexturePackerJSON {
    frames: {
        [key: string]: {
            frame: { x: number, y: number, w: number, h: number };
            rotated: boolean;
            trimmed: boolean;
            spriteSourceSize: { x: number, y: number, w: number, h: number };
            sourceSize: { w: number, h: number };
        }
    };
    meta: {
        app: string;
        version: string;
        image: string;
        format: string;
        size: { w: number, h: number };
        scale: number;
    };
}

/**
 * Custom Graphic wrapper that maintains constant dimensions regardless of content.
 * This ensures that anchor-based centering uses the original (untrimmed) frame size.
 */
class TrimmedSpriteWrapper extends ex.Graphic {
    private sprite: ex.Sprite;
    private offsetX: number;
    private offsetY: number;
    private fullWidth: number;
    private fullHeight: number;

    constructor(sprite: ex.Sprite, offsetX: number, offsetY: number, fullWidth: number, fullHeight: number) {
        super();
        this.sprite = sprite;
        this.offsetX = offsetX;
        this.offsetY = offsetY;
        this.fullWidth = fullWidth;
        this.fullHeight = fullHeight;
    }

    get width(): number {
        return this.fullWidth * Math.abs(this.scale.x);
    }

    set width(value: number) {
        this.fullWidth = value;
    }

    get height(): number {
        return this.fullHeight * Math.abs(this.scale.y);
    }

    set height(value: number) {
        this.fullHeight = value;
    }

    protected _drawImage(ctx: ex.ExcaliburGraphicsContext, x: number, y: number): void {
        // Draw the sprite at the offset position within the full frame
        this.sprite.draw(ctx, x + this.offsetX, y + this.offsetY);
    }

    clone(): TrimmedSpriteWrapper {
        return new TrimmedSpriteWrapper(
            this.sprite.clone(),
            this.offsetX,
            this.offsetY,
            this.fullWidth,
            this.fullHeight
        );
    }
}

/**
 * Loads a Texture Packer JSON and Image, returning a dictionary of Graphics.
 * Uses custom wrapper to handle trimmed frames correctly - this ensures the
 * graphic reports the original (untrimmed) dimensions for proper anchor-based centering.
 * 
 * @param imageSource The loaded Excalibur ImageSource
 * @param jsonResource The loaded Excalibur Resource containing the JSON data
 */
export function parseTextureAtlas(
    imageSource: ex.ImageSource,
    jsonResource: ex.Resource<TexturePackerJSON>
): Record<string, ex.Graphic> {
    const graphics: Record<string, ex.Graphic> = {};

    // Ensure data is loaded
    if (!jsonResource.isLoaded()) {
        throw new Error(`Atlas JSON is not loaded: ${jsonResource.path}`);
    }

    const frames = jsonResource.data.frames;

    for (const [filename, data] of Object.entries(frames)) {
        // Remove file extension for cleaner keys
        const cleanName = filename.replace(/\.(png|jpg|jpeg)$/, "");

        // Get the trim offsets - where the actual content sits within the original frame
        const offsetX = data.spriteSourceSize.x;
        const offsetY = data.spriteSourceSize.y;

        // Get the original (untrimmed) frame dimensions
        const fullWidth = data.sourceSize.w;
        const fullHeight = data.sourceSize.h;

        // Create the base sprite from the trimmed region of the atlas
        const sprite = new ex.Sprite({
            image: imageSource,
            sourceView: {
                x: data.frame.x,
                y: data.frame.y,
                width: data.frame.w,
                height: data.frame.h
            },
            destSize: {
                width: data.frame.w,
                height: data.frame.h
            }
        });

        // Wrap the sprite to report full (untrimmed) dimensions
        // This ensures anchor-based centering uses consistent dimensions
        const wrapper = new TrimmedSpriteWrapper(sprite, offsetX, offsetY, fullWidth, fullHeight);

        graphics[cleanName] = wrapper;
    }

    return graphics;
}

/**
 * Helper to create an Animation from an atlas, assuming frame names are sorted keys.
 * @param imageSource Atlas image
 * @param jsonResource Atlas JSON
 * @param fps Frames per second
 * @param strategy Animation strategy (Loop, Freeze, End, PingPong)
 */
export function createPackedAnimation(
    imageSource: ex.ImageSource,
    jsonResource: ex.Resource<TexturePackerJSON>,
    fps: number = 10,
    strategy: ex.AnimationStrategy = ex.AnimationStrategy.Loop
): ex.Animation {
    const graphics = parseTextureAtlas(imageSource, jsonResource);
    const keys = Object.keys(graphics).sort(); // Sort alphabetically

    const frames = keys.map(key => ({
        graphic: graphics[key],
        duration: 1000 / fps
    }));

    return new ex.Animation({
        frames: frames,
        strategy: strategy
    });
}

/**
 * Helper to create an Animation from multiple atlas pages (multipack).
 * @param sources List of image/json pairs
 * @param fps Frames per second
 */
export function createMultiPackedAnimation(
    sources: { image: ex.ImageSource, json: ex.Resource<TexturePackerJSON> }[],
    fps: number = 10
): ex.Animation {
    let allGraphics: Record<string, ex.Graphic> = {};

    sources.forEach(source => {
        const graphics = parseTextureAtlas(source.image, source.json);
        allGraphics = { ...allGraphics, ...graphics };
    });

    const keys = Object.keys(allGraphics).sort();

    const frames = keys.map(key => ({
        graphic: allGraphics[key],
        duration: 1000 / fps
    }));

    return new ex.Animation({
        frames: frames
    });
}
